// Shared Cloudflare Stream player hook + helpers.
//
// Used by:
//   - LandingV2.jsx `DashboardMockup` (hero tutorial video)
//   - Quiz.jsx `VideoStep` (post-quiz app walkthrough)
//
// Why extract? Both callers need the exact same tap-to-pause + custom
// scrubber + failure-recovery behaviour, and duplicating ~80 lines of
// state-machine code twice would drift out of sync on the next bug fix.
//
// The important trick: the SDK effect depends on the iframe ELEMENT
// (via a state-backed callback ref), not a `useRef` — refs don't
// trigger re-runs so a conditionally-rendered iframe would leave the
// SDK uninitialised forever. See `useCloudflareStreamPlayer`.

import { useEffect, useState } from "react";

// The `sdk.latest.js` bundle is served from embed.cloudflarestream.com
// and returns a `window.Stream(iframe)` factory that gives us a wrapped
// HTMLMediaElement-ish object with `play()`, `pause()`, `currentTime`,
// `duration`, event listeners for `play`/`pause`/`error`/`timeupdate`
// /`loadedmetadata`, and mute controls.
const CF_STREAM_SDK_URL = "https://embed.cloudflarestream.com/embed/sdk.latest.js";

// Build a Cloudflare Stream iframe URL with controls disabled — we
// render our own scrubber + tap-to-pause overlay. `autoplay=true` is a
// belt-and-suspenders fallback so playback still starts inside the
// original user gesture even if the SDK is slow to load.
// Safari / any iOS browser (incl. WhatsApp/Instagram in-app + our iOS app): unmuted
// autoplay inside a cross-origin iframe is blocked and Stream shows no button with
// controls=false → endless spinner. Those go straight to native controls.
export function prefersNativeStreamControls() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
  const desktopSafari = /Safari/i.test(ua) && !/Chrome|Chromium|CriOS|FxiOS|Edg|OPR|Android/i.test(ua);
  return iOS || desktopSafari;
}

// `native: true` → Cloudflare's own controls, no autoplay: the user taps play INSIDE the
// iframe, which every browser accepts (fallback when our custom play gets blocked).
export function buildStreamIframeUrl({ videoId, subdomain, primaryColor = "%23f97316", native = false }) {
  const mode = native ? "autoplay=false&controls=true" : "autoplay=true&controls=false";
  return `https://${subdomain}/${videoId}/iframe?preload=metadata&${mode}&primaryColor=${primaryColor}&letterboxColor=%23000`;
}

/**
 * Wraps a Cloudflare Stream iframe DOM element with the Stream SDK and
 * bubbles up play/pause/error/timeupdate/loadedmetadata events. The
 * iframe MUST be attached via a state-backed callback ref, e.g.
 *
 *   const [iframeEl, setIframeEl] = useState(null);
 *   <iframe ref={setIframeEl} src={...} />
 *   const player = useCloudflareStreamPlayer(iframeEl, { onPlay, ... });
 *
 * A plain `useRef` would trap the effect in a stale closure and never
 * re-run when the iframe mounts (typical for click-to-play flows where
 * the iframe is only rendered after the first tap).
 */
export function useCloudflareStreamPlayer(iframeEl, {
  onPlay,
  onPause,
  onError,
  onTimeUpdate,
  onLoadedMetadata,
} = {}) {
  const [player, setPlayer] = useState(null);
  useEffect(() => {
    if (!iframeEl) return;
    const ensureSdk = () =>
      new Promise((resolve) => {
        if (window.Stream) return resolve(window.Stream);
        const existing = document.querySelector(`script[src="${CF_STREAM_SDK_URL}"]`);
        if (existing) return existing.addEventListener("load", () => resolve(window.Stream));
        const s = document.createElement("script");
        s.src = CF_STREAM_SDK_URL;
        s.async = true;
        s.onload = () => resolve(window.Stream);
        document.body.appendChild(s);
      });
    let cancelled = false;
    ensureSdk().then((Stream) => {
      if (cancelled || !Stream) return;
      const p = Stream(iframeEl);
      p.addEventListener("play", () => onPlay && onPlay());
      p.addEventListener("pause", () => onPause && onPause());
      p.addEventListener("error", () => onError && onError());
      p.addEventListener("timeupdate", () => onTimeUpdate && onTimeUpdate(p.currentTime || 0));
      p.addEventListener("loadedmetadata", () => onLoadedMetadata && onLoadedMetadata(p.duration || 0));
      setPlayer(p);
    });
    return () => { cancelled = true; };
  }, [iframeEl]); // eslint-disable-line react-hooks/exhaustive-deps
  return player;
}

// mm:ss formatter — safe for NaN/Infinity (Stream reports Infinity for
// live streams and NaN before metadata loads).
export function formatVideoTime(sec) {
  if (!isFinite(sec) || sec < 0) return "0:00";
  const s = Math.floor(sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}
